/* Every file the demo hands over is built here, in the browser, and every one
   of them says it is simulated. */

const cell = value => {
    const text = value === null || value === undefined ? '' : String(value);
    return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const toCsv = rows => rows.map(row => row.map(cell).join(',')).join('\n');

export const download = (filename, text, type = 'text/csv') => {
    const url = URL.createObjectURL(new Blob([text], { type: `${type};charset=utf-8` }));
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

export const downloadCsv = (filename, rows) => download(filename, toCsv(rows));

const utc = ms => new Date(ms).toISOString();

export const acSnapshotRows = ac => {
    const header = ['Data mode', 'Timestamp UTC', 'Meter ID', 'Path', 'Location', 'Phase', 'Measurement', 'Value', 'Unit', 'Quality', 'Basis'];
    const base = phase => ['SIMULATED', utc(ac.ms), ac.meterTag, ac.path, ac.meter.label, phase];
    const rows = [header];
    const fixed = (value, places = 2) => (value === null ? '' : value.toFixed(places));

    ac.phases.forEach(p => {
        [
            ['Voltage L-N', fixed(p.vRms), 'V RMS', 'True RMS'],
            ['Current', fixed(p.iRms), 'A RMS', 'True RMS'],
            ['Active power', fixed(p.pKw), 'kW', 'Mean instantaneous v x i'],
            ['Apparent power', fixed(p.sKva), 'kVA', 'V RMS x I RMS'],
            ['Reactive power Q1', fixed(p.q1Kvar), 'kvar', 'Fundamental only'],
            ['True power factor', fixed(p.truePf, 4), 'ratio', 'P / S, includes distortion'],
            ['Displacement power factor', fixed(p.dpf, 4), 'ratio', 'Fundamental only'],
            ['Voltage THD', fixed(p.thdV), '%', 'Orders 2-50, fundamental basis'],
            ['Current THD', fixed(p.thdI), '%', 'Orders 2-50, present fundamental current']
        ].forEach(([name, value, unit, basis]) => rows.push([...base(p.name), name, value, unit, 'Simulated', basis]));
    });

    rows.push([...base('Total'), 'Active power', fixed(ac.totalKw), 'kW', 'Simulated', 'Sum of phases']);
    rows.push([...base('Total'), 'Apparent power', fixed(ac.totalKva), 'kVA', 'Simulated', 'Arithmetic sum of phases']);
    rows.push([...base('Neutral'), 'Current', fixed(ac.neutralA), 'A RMS', 'Simulated', 'Instantaneous sum of phase currents']);
    return rows;
};

export const dcSnapshotRows = (dc, scope) => {
    const rows = [['Data mode', 'Timestamp UTC', 'Bus scope', 'Asset', 'Measurement', 'Value', 'Unit', 'Quality', 'Basis']];
    const push = (asset, name, value, unit, basis, quality = 'Simulated') =>
        rows.push(['SIMULATED', utc(dc.ms), scope, asset, name, value, unit, quality, basis]);

    ['A', 'B'].filter(bus => scope === 'both' || scope === bus).forEach(bus => {
        const data = dc.buses[bus];

        push(`Bus ${bus}`, 'Voltage', data.volts.toFixed(2), 'V DC', 'DC mean, pole to pole');
        push(`Bus ${bus}`, 'Current', data.amps.toFixed(1), 'A DC', 'DC mean, positive toward load');
        push(`Bus ${bus}`, 'Power', data.kw.toFixed(2), 'kW', 'Steady-state V x I');
        push(`Bus ${bus}`, 'Loading', data.loading.toFixed(1), '%', 'Current / 4,000 A configured limit');
        push(`Bus ${bus}`, 'Insulation resistance', data.insulation.toFixed(2), 'MOhm', 'Insulation monitor');
        push(`Bus ${bus}`, 'Residual current', data.residual.toFixed(1), 'mA', 'AC+DC RMS, DC-1 kHz');

        data.feeders.forEach(f => {
            push(f.id, 'Voltage', f.volts.toFixed(2), 'V DC', 'DC mean, pole to pole');
            push(f.id, 'Current', f.amps.toFixed(1), 'A DC', 'DC mean, positive toward load');
            push(f.id, 'Power', f.kw.toFixed(2), 'kW', 'Steady-state V x I');
            if (f.stale) push(f.joint, 'Temperature', '', '°C', 'Contact sensor', 'Stale');
            else push(f.joint, 'Temperature', f.temp.toFixed(1), '°C', 'Contact sensor');
        });
    });

    dc.conditions.filter(c => scope === 'both' || c.bus === scope).forEach(c => {
        push(c.asset, `Alarm: ${c.title}`, c.severity, 'state', 'Worked out from the readings against monitoring limits');
    });

    return rows;
};

export const alarmLogRows = log => [
    ['Data mode', 'Time UTC', 'Asset', 'Event', 'State', 'Severity'],
    ...log.map(row => ['SIMULATED', utc(row.at), row.asset, row.event, row.state, row.severity || ''])
];
